// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { match } from "ts-pattern";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { SensitiveDataFilter } from "~/components/ui/sensitive-data-filter";
import { Skeleton } from "~/components/ui/skeleton";
import { isSensitiveTimeWindow } from "~/lib/utils";
import { OryxConversationCard } from "~/modules/cactus-generations/components/oryx-conversation-card";
import { WorkflowAssetPreview } from "~/modules/cactus-generations/components/workflow-asset-preview";
import { fetchWorkflowOutputQuery } from "~/modules/cactus-generations/queries";

export const Route = createFileRoute(
  "/_dash/ai-edits/cactus-tools/$workflowIdentifier/$workflowRunId"
)({
  component: RouteComponent,
  validateSearch: z.object({
    workflow: z.string().optional(),
    page: z.number().optional(),
    view: z.enum(["grid", "table"]).optional(),
    columns: z.string().optional(),
    excludeOryxEmployees: z.boolean().optional(),
    excludeDevAccounts: z.boolean().optional(),
    linqPhoneNumber: z.string().optional(),
    wrap: z.boolean().optional(),
  }),
});

function RouteComponent() {
  const { workflowIdentifier, workflowRunId } = Route.useParams();
  const {
    workflow,
    page,
    view,
    columns,
    wrap,
    linqPhoneNumber,
    excludeOryxEmployees,
    excludeDevAccounts,
  } = Route.useSearch();
  const [previewAspectRatio, setPreviewAspectRatio] = useState<number>(9 / 16);

  const { data, isLoading, error } = fetchWorkflowOutputQuery.useQuery({
    pathParams: { workflowName: workflowIdentifier, runId: workflowRunId },
  });

  const output = data?.output;

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !output) {
    return (
      <div className="space-y-6">
        <Link
          to="/ai-edits/cactus-tools"
          search={{
            workflow,
            page,
            view,
            columns,
            wrap,
            linqPhoneNumber,
            excludeOryxEmployees,
            excludeDevAccounts,
          }}
          className="text-sm text-muted-foreground hover:underline"
        >
          Back to Cactus Tools
        </Link>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              {error ? "Failed to load workflow run details." : "Workflow run not found."} Please
              try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasAssetOutput = !!output.assetUri || !!output.assetId;
  const hasJsonOutput = !!output.jsonResult && Object.keys(output.jsonResult).length > 0;
  const hasWorkflowInput = !!output.workflowInput && Object.keys(output.workflowInput).length > 0;
  const isSensitive = output.userId === "dev-user-id" && isSensitiveTimeWindow(output.createdAt);
  const oryxConversation = output.oryxConversation;
  const hasOryxConversation = oryxConversation !== null;
  const latestConversationTurn = oryxConversation?.turns[oryxConversation.turns.length - 1] ?? null;
  const outputAssetId = output.assetId;
  const outputAssetUri = output.assetUri;
  const previewContent = match({ hasAssetOutput, hasJsonOutput })
    .with({ hasAssetOutput: true }, () => (
      <WorkflowAssetPreview
        assetId={outputAssetId}
        assetUri={outputAssetUri}
        mode="detail"
        onAspectRatioChange={setPreviewAspectRatio}
      />
    ))
    .with({ hasJsonOutput: true }, () => (
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
        <Icon
          name={hasOryxConversation ? "chat" : "metadata"}
          className="mb-2 h-8 w-8 text-muted-foreground"
        />
        <p className="text-center text-sm text-muted-foreground">
          {hasOryxConversation ? "Oryx Conversation" : "JSON Output"}
        </p>
        <p className="mt-1 text-center text-xs text-muted-foreground/70">
          {hasOryxConversation
            ? `${oryxConversation.turns.length} turns${latestConversationTurn ? ` • Latest: ${formatConversationRole(latestConversationTurn.role)}` : ""}`
            : "See details below"}
        </p>
      </div>
    ))
    .otherwise(() => (
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">No output available</p>
      </div>
    ));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/ai-edits/cactus-tools"
          search={{
            workflow,
            page,
            view,
            columns,
            wrap,
            linqPhoneNumber,
            excludeOryxEmployees,
            excludeDevAccounts,
          }}
          className="text-sm text-muted-foreground hover:underline"
        >
          Back to Cactus Tools
        </Link>
      </div>

      <div>
        <button
          className="text-xl font-bold cursor-pointer"
          onClick={() => {
            navigator.clipboard.writeText(workflowRunId);
            toast.success("Workflow Run ID copied to clipboard");
          }}
        >
          {`Workflow Run: ${workflowRunId}`}
        </button>
      </div>

      <div className="flex flex-row gap-6">
        <div className="w-80 shrink-0">
          <div
            className="relative w-full bg-muted rounded-lg overflow-hidden"
            style={{ aspectRatio: previewAspectRatio }}
          >
            {previewContent}
          </div>
        </div>

        <Card className="flex-1">
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-3">
              {output.assetId && (
                <>
                  <p className="text-sm font-medium text-muted-foreground">Asset ID</p>
                  <button
                    className="text-left"
                    onClick={() => {
                      navigator.clipboard.writeText(output.assetId!);
                      toast.success("Asset ID copied");
                    }}
                  >
                    <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:bg-muted/80 cursor-pointer">
                      {output.assetId}
                    </code>
                  </button>
                </>
              )}

              {output.inputAssetId && (
                <>
                  <p className="text-sm font-medium text-muted-foreground">Input Asset ID</p>
                  <button
                    className="text-left"
                    onClick={() => {
                      navigator.clipboard.writeText(output.inputAssetId!);
                      toast.success("Input Asset ID copied");
                    }}
                  >
                    <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:bg-muted/80 cursor-pointer">
                      {output.inputAssetId}
                    </code>
                  </button>
                </>
              )}

              <p className="text-sm font-medium text-muted-foreground">User ID</p>
              <Link
                to="/users/view/$userId"
                params={{ userId: output.userId }}
                className="flex items-center gap-1"
              >
                <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:underline">
                  {output.userId}
                </code>
                <Icon name="link-out" className="h-3 w-3" />
              </Link>

              <p className="text-sm font-medium text-muted-foreground">Org ID</p>
              {output.orgId ? (
                <Link
                  to="/organizations/view/$orgId"
                  params={{ orgId: output.orgId }}
                  className="flex items-center gap-1"
                >
                  <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:underline">
                    {output.orgId}
                  </code>
                  <Icon name="link-out" className="h-3 w-3" />
                </Link>
              ) : (
                <code className="text-xs bg-muted px-2 py-1 rounded break-all">N/A</code>
              )}

              <p className="text-sm font-medium text-muted-foreground">Workflow</p>
              <Badge variant="secondary" className="w-fit">
                {output.workflowName}
              </Badge>

              <p className="text-sm font-medium text-muted-foreground">Workflow Run ID</p>
              <button
                className="text-left"
                onClick={() => {
                  navigator.clipboard.writeText(output.workflowRunId);
                  toast.success("Workflow Run ID copied");
                }}
              >
                <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:bg-muted/80 cursor-pointer">
                  {output.workflowRunId}
                </code>
              </button>

              {output.traceId && (
                <>
                  <p className="text-sm font-medium text-muted-foreground">Trace ID</p>
                  <button
                    className="text-left"
                    onClick={() => {
                      navigator.clipboard.writeText(output.traceId!);
                      toast.success("Trace ID copied");
                    }}
                  >
                    <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:bg-muted/80 cursor-pointer">
                      {output.traceId}
                    </code>
                  </button>
                </>
              )}

              {output.durationSeconds != null && (
                <>
                  <p className="text-sm font-medium text-muted-foreground">Duration</p>
                  <p className="text-sm">{output.durationSeconds.toFixed(2)}s</p>
                </>
              )}

              <p className="text-sm font-medium text-muted-foreground">Created At</p>
              <SensitiveDataFilter isFiltered={isSensitive}>
                <div className="flex flex-col">
                  <p className="text-sm">{formatDate(output.createdAt)}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatRelativeTime(output.createdAt)}
                  </p>
                </div>
              </SensitiveDataFilter>

              {output.updatedAt && (
                <>
                  <p className="text-sm font-medium text-muted-foreground">Updated At</p>
                  <SensitiveDataFilter isFiltered={isSensitive}>
                    <div className="flex flex-col">
                      <p className="text-sm">{formatDate(output.updatedAt)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatRelativeTime(output.updatedAt)}
                      </p>
                    </div>
                  </SensitiveDataFilter>
                </>
              )}

              <p className="text-sm font-medium text-muted-foreground">Expires At</p>
              <SensitiveDataFilter isFiltered={isSensitive}>
                <div className="flex flex-col">
                  <p className="text-sm">{formatDate(output.expiresAt)}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatRelativeTime(output.expiresAt)}
                  </p>
                </div>
              </SensitiveDataFilter>
            </div>
          </CardContent>
        </Card>
      </div>

      {hasOryxConversation && <OryxConversationCard conversation={oryxConversation} />}

      {hasJsonOutput && !hasAssetOutput && !hasOryxConversation && (
        <JsonDebugCard title="JSON Output" value={output.jsonResult} />
      )}

      {hasJsonOutput && hasOryxConversation && (
        <Collapsible>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-base">Raw JSON</CardTitle>
              <CollapsibleTrigger className="text-sm text-muted-foreground hover:text-foreground">
                Toggle
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                <pre className="max-h-96 overflow-auto rounded-lg bg-muted p-4 text-xs">
                  <code>{JSON.stringify(output.jsonResult, null, 2)}</code>
                </pre>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {hasWorkflowInput && <JsonDebugCard title="Workflow Input" value={output.workflowInput} />}
    </div>
  );
}

function JsonDebugCard({ title, value }: { title: string; value: Record<string, unknown> | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="max-h-96 overflow-auto rounded-lg bg-muted p-4 text-xs">
          <code>{JSON.stringify(value, null, 2)}</code>
        </pre>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-6 w-64" />
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function formatDate(timestampMs: number): string {
  const date = new Date(timestampMs);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeTime(timestampMs: number): string {
  const now = Date.now();
  const diffMs = timestampMs - now;
  const isPast = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  const seconds = Math.floor(absDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (isPast) {
    if (seconds < 60) {
      return "just now";
    }
    if (minutes < 60) {
      return `${minutes} minutes ago`;
    }
    if (hours < 24) {
      return `${hours} hours ago`;
    }
    if (days < 30) {
      return `${days} days ago`;
    }
    return `${Math.floor(days / 30)} months ago`;
  } else {
    if (seconds < 60) {
      return "in a moment";
    }
    if (minutes < 60) {
      return `in ${minutes} minutes`;
    }
    if (hours < 24) {
      return `in ${hours} hours`;
    }
    if (days < 30) {
      return `in ${days} days`;
    }
    return `in ${Math.floor(days / 30)} months`;
  }
}

function formatConversationRole(role: string) {
  if (role === "assistant") {
    return "Oryx";
  }

  if (role === "tool") {
    return "Tool";
  }

  return "User";
}
